import { track } from "@amplitude/analytics-react-native";
import { toast } from "burnt";
import { amp } from "src/api/amplitude";
import FaceFilterMode from "src/api/graphql/__generated__/enums/FaceFilterMode";
import PhotoState from "src/api/graphql/__generated__/enums/PhotoState";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import {
	GatheringHeaderInfoFragmentFragment,
	SignedUrl,
} from "src/api/graphql/__generated__/graphql";
import isClip from "src/constants/isClip";
import isWeb from "src/constants/isWeb";
import { getClientId } from "src/helpers/globalHelpers";
import { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import SortModeQueryParamLookup from "./constants/SortModeQueryParamLookup";
import ValidCaptureTimeViewModes from "./constants/ValidCaptureTimeViewModes";
import {
	GatheringEditMode,
	GatheringSelectMode,
	GatheringSortMode,
	GatheringViewMode,
	SelectedPhoto,
} from "./types";

const PHOTO_SELECT_LIMIT = isWeb || isClip ? 100 : 9999999;
interface SelectPhotoInfo {
	photoId: string;
	ownerId: string;
	readUrl: SignedUrl | undefined | null;
	variant: PhotoVariant | undefined | null;
	isTopPhoto: boolean;
	state: PhotoState | undefined | null;
}

export type FilterSortSelections = {
	sortMode: GatheringStore["sortMode"];
	filterPhotoVariant: GatheringStore["filterPhotoVariant"];
	filterUserIds: GatheringStore["filterUserIds"];
	filterUploaderIds: GatheringStore["filterUploaderIds"];
	filterLabels: GatheringStore["filterLabels"];
	filterRestrictedLabels: GatheringStore["filterRestrictedLabels"];
	numColumnsTarget: GatheringStore["numColumnsTarget"];
};

interface GatheringProperties extends NonNullable<GatheringHeaderInfoFragmentFragment> {}

export interface GatheringStore extends GatheringProperties {
	identifier: string;
	selectedPhotos: Record<string, SelectedPhoto>;
	selectMode: GatheringSelectMode;
	editMode: GatheringEditMode;
	sortMode: GatheringSortMode;
	isEditing: boolean;
	filterUserIds: string[];
	filterUploaderIds: string[];
	filterPhotoVariant: PhotoVariant[];
	filterMode: FaceFilterMode;
	filterLabels: string[];
	filterRestrictedLabels: string[];
	isHoveringImageFile: boolean;
	loadingForNavBar: boolean;
	viewMode: GatheringViewMode;
	hasTopPhotos: boolean | undefined;
	hasPhotos: boolean | undefined;
	/**
	 * Goal number of columns of photos to display
	 *
	 * May or may not be the real number depending on screen size
	 */
	numColumnsTarget: number | undefined;
	previewPhotos: GatheringOrLocalPhoto[];
	setLoadingForNavBar: (loading: boolean) => void;
	toggleFilterUserId: (userId: string) => void;
	getAllFilterSortSelections: () => FilterSortSelections;
	setFiltersAndSort: (selection: Partial<FilterSortSelections>) => void;
	setFilterMode: (mode: FaceFilterMode) => void;
	getQueryObject: () => {
		uIds?: string;
		uploaders?: string;
		media?: string;
		edit?: GatheringEditMode;
		view?: GatheringViewMode;
		filter?: FaceFilterMode;
		sort?: GatheringSortMode;
		tags?: string;
		adminTags?: string;
		columns?: number;
	};
	/**
	 * Encodes important part of state into query params. Specifically, filterBy
	 */
	getQueryParams: () => string | null;
	togglePhotoSelect: (props: SelectPhotoInfo) => void;
	resetFaceFilters: () => void;
	selectPhoto: (props: SelectPhotoInfo) => void;
	deselectPhoto: (props: { photoId: string }) => void;
	setSelectMode: (mode: GatheringSelectMode) => void;
	resetSelectMode: () => void;
	setEditMode: (mode: GatheringEditMode) => void;
	setViewMode: (mode: GatheringViewMode) => void;
	reset: () => void;
	setIsHoveringImageFile: (isHoveringImageFile: boolean) => void;
	headerHeight: number | null;
	setHeaderHeight: (height: number) => void;
	/**
	 * Keeping track of single selected photos for drag and select
	 */
	photoSingleSelectedLookup: Record<string, boolean>;
	setSingleSelected: (photoId: string, isSelected: boolean) => void;
	hasPhotoSelectPopUpOpen: boolean;
	editGatheringNameCallback: null | ((action: "Cancel" | "Save") => Promise<void>);
}

type GatheringState = Pick<
	GatheringStore,
	| "selectedPhotos"
	| "selectMode"
	| "filterUserIds"
	| "filterUploaderIds"
	| "filterPhotoVariant"
	| "filterMode"
	| "filterLabels"
	| "filterRestrictedLabels"
	| "sortMode"
	| "isHoveringImageFile"
	| "editMode"
	| "viewMode"
	| "hasTopPhotos"
	| "hasPhotos"
	| "isEditing"
	| "loadingForNavBar"
	| "headerHeight"
	| "photoSingleSelectedLookup"
	| "hasPhotoSelectPopUpOpen"
	| "editGatheringNameCallback"
	| "numColumnsTarget"
	| "previewPhotos"
>;

const emptyState: GatheringState = {
	selectedPhotos: {},
	selectMode: "None",
	filterUserIds: [],
	filterUploaderIds: [],
	filterPhotoVariant: [],
	filterMode: FaceFilterMode.Or,
	sortMode: "UploadedTime",
	isHoveringImageFile: false,
	editMode: "None",
	viewMode: "All",
	filterLabels: [],
	filterRestrictedLabels: [],
	hasTopPhotos: undefined,
	hasPhotos: undefined,
	isEditing: false,
	loadingForNavBar: false,
	headerHeight: null,
	photoSingleSelectedLookup: {},
	hasPhotoSelectPopUpOpen: false,
	editGatheringNameCallback: null,
	numColumnsTarget: undefined,
	previewPhotos: [],
};

const getPartialFromEditMode = (
	editMode: GatheringEditMode | undefined,
): Partial<GatheringState> => {
	if (!editMode) return {};
	return {
		editMode: editMode,
		isEditing: editMode !== "None",
	};
};

const getPartialFromSelectMode = (
	selectMode: GatheringSelectMode | undefined,
): Partial<GatheringState> => {
	if (!selectMode) return {};
	return {
		selectedPhotos: {},
		selectMode: selectMode,
	};
};

const getToggledArrayValue = <T>(newValue: T, filterArr: T[]) => {
	if (filterArr.includes(newValue)) {
		const newFilterArr = filterArr.filter((value) => value !== newValue);
		return newFilterArr;
	}
	return [...filterArr, newValue];
};

const determineViewModeFromFilters = ({
	filterPhotoVariant,
	filterUploaderIds,
	filterUserIds,
	filterLabels,
	filterRestrictedLabels,
}: GatheringState): GatheringViewMode => {
	if (
		filterPhotoVariant.length > 0 ||
		filterUploaderIds.length > 0 ||
		filterUserIds.length > 0 ||
		filterLabels.length > 0 ||
		filterRestrictedLabels.length > 0
	) {
		return "Filter";
	} else {
		return "All";
	}
};

export const createGatheringStore = (
	initialState: Partial<GatheringState> & GatheringProperties,
) => {
	return createStore<GatheringStore>()(
		subscribeWithSelector((set, get) => ({
			...emptyState,
			...initialState,
			...getPartialFromEditMode(initialState.editMode),
			...getPartialFromSelectMode(initialState.selectMode),
			identifier: initialState.refCode ?? initialState.gatheringId,
			reset: () => {
				set(() => {
					return {
						...emptyState,
					};
				});
			},
			togglePhotoSelect: (args) => {
				const { selectedPhotos, selectPhoto, deselectPhoto } = get();
				if (selectedPhotos.hasOwnProperty(args.photoId)) {
					deselectPhoto({ photoId: args.photoId });
				} else {
					selectPhoto(args);
				}
			},
			resetFaceFilters: () => {
				set(() => {
					return {
						filterUserIds: [],
						filterMode: FaceFilterMode.Or,
					};
				});
			},
			selectPhoto: ({ photoId, ownerId, readUrl, variant, isTopPhoto, state }) => {
				const { selectMode, selectedPhotos } = get();
				const photo: SelectedPhoto = {
					photoId,
					ownerId,
					readUrl,
					variant: variant ?? PhotoVariant.Image,
					isTopPhoto,
					state,
				};
				if (selectedPhotos.hasOwnProperty(photoId)) return;
				switch (selectMode) {
					case "None":
						return;
					case "Select":
						return set((state) => {
							track(amp.gathering.selectPhoto, {
								numSelected: Object.keys(state.selectedPhotos).length + 1,
							});
							state.selectedPhotos[photoId] = photo;
							if (Object.keys(state.selectedPhotos).length > PHOTO_SELECT_LIMIT) {
								delete state.selectedPhotos[photoId];
								if (!state.hasPhotoSelectPopUpOpen) {
									track(amp.gathering.selectLimit, {
										numSelected: Object.keys(state.selectedPhotos).length,
									});
									set(() => ({
										hasPhotoSelectPopUpOpen: true,
									}));

									toast({
										title: "Woah! Slow down",
										message: `On swsh you can select ${PHOTO_SELECT_LIMIT} photos at a time`,
										preset: "error",
									});
								}
							}
							return {
								selectedPhotos: {
									...state.selectedPhotos,
								},
							};
						});
					default:
						return enumNever(selectMode);
				}
			},
			deselectPhoto: ({ photoId }) => {
				const { selectMode, selectedPhotos } = get();
				if (!selectedPhotos.hasOwnProperty(photoId)) return;
				set((state) => {
					switch (selectMode) {
						case "None":
							return {};
						case "Select":
							track(amp.gathering.deselectPhoto, {
								numSelected: Object.keys(state.selectedPhotos).length - 1,
							});
							delete state.selectedPhotos[photoId];
							return {
								selectedPhotos: {
									...state.selectedPhotos,
								},
							};
						default:
							return enumNever(selectMode);
					}
				});
			},
			setSelectMode: (mode: GatheringSelectMode) => {
				set(() => {
					track(amp.gathering.changeSelectMode, {
						mode,
					});
					return getPartialFromSelectMode(mode);
				});
			},
			resetSelectMode: () => {
				set(() => {
					return getPartialFromSelectMode("None");
				});
			},
			setEditMode: (mode: GatheringEditMode) => {
				set((state) => {
					track(amp.gathering.changeEditMode, {
						mode,
					});
					return getPartialFromEditMode(mode);
				});
			},
			setViewMode: (mode: GatheringViewMode) => {
				set((state) => {
					if (state.viewMode === mode) return {};
					track(amp.gathering.changeViewMode, {
						mode,
					});
					const newState: Partial<GatheringState> = {
						viewMode: mode,
					};
					if (mode === "Filter") {
						newState.filterUserIds = [getClientId()];
					} else {
						newState.filterUserIds = [];
					}
					// Upload time sort only supported for a limited set of modes
					if (state.sortMode === "CaptureTime" && !ValidCaptureTimeViewModes.has(mode)) {
						newState.sortMode = "UploadedTime";
					}
					return newState;
				});
			},
			getQueryObject: () => {
				const {
					filterUserIds,
					filterUploaderIds,
					filterPhotoVariant,
					filterMode,
					editMode,
					viewMode,
					sortMode,
					filterLabels,
					filterRestrictedLabels,
					numColumnsTarget,
				} = get();
				const data: {
					uIds?: string;
					uploaders?: string;
					media?: string;
					edit?: GatheringEditMode;
					view?: GatheringViewMode;
					filter?: FaceFilterMode;
					sort?: GatheringSortMode;
					tags?: string;
					adminTags?: string;
					columns?: number;
				} = {};

				if (filterUserIds && filterUserIds.length > 0) {
					data.uIds = JSON.stringify(filterUserIds);
				}
				if (filterUploaderIds && filterUploaderIds.length > 0) {
					data.uploaders = JSON.stringify(filterUploaderIds);
				}
				if (filterPhotoVariant && filterPhotoVariant.length > 0) {
					data.media = JSON.stringify(filterPhotoVariant);
				}
				if (filterMode && filterMode !== FaceFilterMode.Or) {
					data.filter = filterMode;
				}
				if (editMode !== "None") {
					data.edit = editMode;
				}
				if (viewMode !== "All" && viewMode !== "Filter") {
					data.view = viewMode;
				}
				if (sortMode !== "UploadedTime") {
					data.sort = sortMode;
				}
				if (filterLabels && filterLabels.length > 0) {
					data.tags = JSON.stringify(filterLabels);
				}
				if (filterRestrictedLabels && filterRestrictedLabels.length > 0) {
					data.adminTags = JSON.stringify(filterRestrictedLabels);
				}
				if (numColumnsTarget) {
					data.columns = numColumnsTarget;
				}

				return data;
			},
			getQueryParams: () => {
				const data = get().getQueryObject();
				const strComponents: string[] = [];
				if (data.uIds && data.uIds.length > 0) {
					strComponents.push(`uIds=${encodeURIComponent(data.uIds)}`);
				}
				if (data.filter) {
					strComponents.push(`filter=${encodeURIComponent(data.filter)}`);
				}
				if (data.uploaders && data.uploaders.length > 0) {
					strComponents.push(`uploaders=${encodeURIComponent(data.uploaders)}`);
				}
				if (data.media && data.media.length > 0) {
					strComponents.push(`media=${encodeURIComponent(data.media)}`);
				}
				if (data.edit) {
					strComponents.push(`edit=${encodeURIComponent(data.edit)}`);
				}
				if (data.view) {
					const parsedView = data.view === "AllCaller" ? "Uploads" : data.view;
					strComponents.push(`view=${encodeURIComponent(parsedView)}`);
				}
				if (data.sort) {
					strComponents.push(
						`sort=${encodeURIComponent(SortModeQueryParamLookup[data.sort])}`,
					);
				}
				if (data.tags && data.tags.length > 0) {
					strComponents.push(`tags=${encodeURIComponent(data.tags)}`);
				}
				if (data.adminTags && data.adminTags.length > 0) {
					strComponents.push(`adminTags=${encodeURIComponent(data.adminTags)}`);
				}
				if (data.columns) {
					strComponents.push(`columns=${encodeURIComponent(data.columns)}`);
				}
				return strComponents.length > 0 ? strComponents.join("&") : null;
			},
			setIsHoveringImageFile: (isHoveringImageFile) => {
				set(() => {
					track(
						isHoveringImageFile
							? amp.gathering.startImageHover
							: amp.gathering.endImageHover,
					);
					return {
						isHoveringImageFile,
					};
				});
			},
			toggleFilterUserId: (userId) => {
				set((state) => {
					return {
						filterUserIds: getToggledArrayValue(userId, state.filterUserIds),
						viewMode: determineViewModeFromFilters(state),
					};
				});
			},
			getAllFilterSortSelections: () => {
				const {
					sortMode,
					filterPhotoVariant,
					filterUploaderIds,
					filterUserIds,
					filterLabels,
					filterRestrictedLabels,
					numColumnsTarget,
				} = get();
				return {
					sortMode,
					filterPhotoVariant,
					filterUploaderIds,
					filterUserIds,
					filterLabels,
					filterRestrictedLabels,
					numColumnsTarget,
				};
			},
			setFiltersAndSort: ({
				sortMode,
				filterUserIds,
				filterUploaderIds,
				filterPhotoVariant,
				filterLabels,
				filterRestrictedLabels,
				numColumnsTarget,
			}) => {
				set((state) => {
					track("gathering_filterSort_apply", {
						sortMode,
						numUserIds: filterUserIds?.length,
						numUploaderIds: filterUploaderIds?.length,
						photoVariants: filterPhotoVariant,
						numLabels: filterLabels?.length,
						numRestrictedLabels: filterRestrictedLabels?.length,
						numColumnsTarget,
					});
					const newState: Partial<GatheringState> = {
						sortMode: sortMode ?? "UploadedTime",
						filterUserIds: filterUserIds ?? [],
						filterUploaderIds: filterUploaderIds ?? [],
						filterPhotoVariant: filterPhotoVariant ?? [],
						filterLabels: filterLabels ?? [],
						filterRestrictedLabels: filterRestrictedLabels ?? [],
						numColumnsTarget,
					};
					newState.viewMode = determineViewModeFromFilters({ ...state, ...newState });
					if (
						sortMode === "CaptureTime" &&
						!ValidCaptureTimeViewModes.has(state.viewMode)
					) {
						newState.viewMode = "All";
					}
					return newState;
				});
			},
			setFilterMode: (mode) => {
				set(() => {
					track(amp.gathering.changeFilterMode, {
						mode,
					});
					return {
						filterMode: mode,
					};
				});
			},
			setLoadingForNavBar: (loading) => {
				set(() => {
					return {
						loadingForNavBar: loading,
					};
				});
			},
			setHeaderHeight: (height) => {
				set(() => {
					return {
						headerHeight: height,
					};
				});
			},
			setSingleSelected: (photoId, isSelected) => {
				set((state) => {
					state.photoSingleSelectedLookup[photoId] = isSelected;
					return {
						photoSingleSelectedLookup: state.photoSingleSelectedLookup,
					};
				});
			},
		})),
	);
};
